import { ListOrItem } from "react-daisyui/dist/types";

import api from "../api";

export type AddBenefitPayload = {
  color?: string;
  description: string;
  icon: string;
  isActive?: boolean;
  title: string;
};

export type SafetyTip = {
  hidden: boolean;
  id: number;
  tip: string;
  updated_at: string;
};

export type Scooter = {
  battery_level(battery_level: any): number;
  capacity(capacity: any): number;
  description: any;
  features: any;
  id: number;
  image_path: string;
  image_url: string;
  location: any;
  model: string;
  name: string;
  price_per_hour(price_per_hour: any): number;
  ready_time: any;
  status: string;
  updated_at: string;
};

export type ScooterBenefit = {
  color?: string;
  description: string;
  hidden: boolean;
  icon: string;
  id: string;
  isActive?: boolean;
  title: string;
  updated_at: string;
};

// --- Types ---
export type ScooterFee = {
  created_at?: string;
  id: number;
  price_per_minute: string;
  unlock_fee: string;
  updated_at?: string;
  updated_by?: string;
};

export type SectionConfig = {
  config: any;
  id: number;
  updated_at: string;
};

export async function addBenefit(
  benefit: Omit<ScooterBenefit, "hidden" | "id" | "updated_at">,
): Promise<ScooterBenefit> {
  const res = await api.post("/scooter/benefits", benefit);
  return res.data.benefit ?? res.data;
}

export async function addSafetyTip(payload: {
  color?: string;
  description: string;
  icon: string;
  is_hidden?: boolean;
  title: string;
}) {
  const res = await api.post("/scooter/safety-tips", payload);
  return res.data.tip;
}

export async function addScooter(data: FormData): Promise<any> {
  const res = await api.post("/scooter/create", data, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.scooter ?? res.data;
}

export async function addSectionConfig(
  formData: FormData,
): Promise<SectionConfig> {
  const res = await api.post("/scooter/section-config", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.config ?? res.data;
}

export async function deleteBenefit(id: number): Promise<void> {
  await api.delete(`/scooter/benefits/${id}`);
}

export async function deleteSafetyTip(id: number): Promise<void> {
  await api.delete(`/scooter/safety-tips/${id}`);
}

export async function deleteScooter(id: number | string): Promise<void> {
  await api.delete(`/scooter/delete/${id}`);
}

// --- Benefits ---
export async function fetchBenefits(p0: string): Promise<ScooterBenefit[]> {
  const res = await api.get("/scooter/benefits");
  return res.data.benefits ?? res.data;
}

// --- Fees ---
export async function fetchFees(): Promise<null | ScooterFee> {
  const res = await api.get("/scooter/fees");
  return res.data.fee ?? null;
}

// --- Safety Tips ---
export async function fetchSafetyTips(): Promise<SafetyTip[]> {
  const res = await api.get("/scooter/safety-tips");
  return res.data.tips.map((tip: any, idx: number) => ({
    color: tip.color ?? "success",
    description: tip.description ?? "",
    icon: tip.icon ?? "HiShieldCheck",
    id: tip.id?.toString() ?? `${idx}`,
    isActive:
      tip.is_hidden == "0" || tip.is_hidden == false || tip.is_hidden == 0, // map is_hidden to isActive
    order: typeof tip.order === "number" ? tip.order : idx,
    title: tip.title ?? "",
  }));
}

// --- Scooters ---
export async function fetchScooters(): Promise<Scooter[]> {
  const res = await api.get("/scooters");
  return res.data.scooters ?? res.data;
}

// --- Section Config ---
export async function fetchSectionConfig() {
  const res = await api.get("/scooter/section-config");
  return res.data.config;
}

export async function hideBenefit(id: number): Promise<ScooterBenefit> {
  const res = await api.post(`/scooter/benefits/${id}/hide`);
  return res.data.benefit ?? res.data;
}

export async function hideSafetyTip(id: number): Promise<SafetyTip> {
  const res = await api.post(`/scooter/safety-tips/${id}/hide`);
  return res.data.tip ?? res.data;
}

export async function setFees(
  fee: Omit<ScooterFee, "id" | "updated_at">,
): Promise<ScooterFee> {
  const res = await api.post("/scooter/fees", fee);
  return res.data.fee ?? res.data;
}

export async function unhideBenefit(id: number): Promise<ScooterBenefit> {
  const res = await api.post(`/scooter/benefits/${id}/unhide`);
  return res.data.benefit ?? res.data;
}

export async function unhideSafetyTip(id: number): Promise<SafetyTip> {
  const res = await api.post(`/scooter/safety-tips/${id}/unhide`);
  return res.data.tip ?? res.data;
}

export async function updateBenefit(
  id: string,
  benefit: Partial<ScooterBenefit>,
): Promise<ScooterBenefit> {
  const res = await api.put(`/scooter/benefits/${id}`, benefit);
  return res.data.benefit ?? res.data;
}

export async function updateSafetyTip(
  id: number | string,
  payload: {
    color?: string;
    description: string;
    icon: string;
    is_hidden?: boolean;
    title: string;
  },
) {
  const res = await api.post(`/scooter/safety-tips/${id}`, payload);
  return res.data.tip;
}

export async function updateScooter(
  id: number | string,
  data: FormData,
): Promise<any> {
  const res = await api.post(`/scooter/update/${id}`, data, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.scooter ?? res.data;
}

export async function updateSectionConfig(
  id: number,
  config: Partial<SectionConfig>,
): Promise<SectionConfig> {
  const res = await api.put(`/scooter/section-config/${id}`, config);
  return res.data.config ?? res.data;
}
